import { ForwardRefExoticComponent } from 'react';
import * as R from 'remeda';
import {
  IconBolt,
  IconLayoutGrid,
  IconMessageCirclePlus,
  IconProps,
  IconSTurnRight,
} from '@rossum/ui/icons/tabler';
import { Prettify } from '../../lib/typeUtils';
import { GroupRole } from '../../types/group';

// TODO: It's a bit weird that this would sit inside `/features` but it's the only way to keep it all together
// and get good types
export const allFeaturesListBase = [
  {
    id: 'discovery-calls',
    type: 'internal',
    description: 'This flag enables discovery call popups (Calendly).',
    owner: 'Jan Marsicek',
  } as const,
  {
    id: 'optimize-automation-setup',
    type: 'internal',
    description:
      'This flag enables the optimization feature of automation page in queue settings.',
    owner: 'Serhan Guney',
  } as const,
  {
    id: 'open-telemetry',
    type: 'internal',
    description:
      'Feature flag used for enabling OpenTelemetry so frontend starts sending telemetry metrics.',
    owner: 'Serhan Guney',
  } as const,
  {
    id: 'mat-1949-splitting-view',
    type: 'internal',
    description:
      'Enables the new splitting screen route (/split/:annotationId), rebuilt on the shared canvas.',
    epic: 'MAT-1949',
    owner: 'Martin Brodziansky',
  } as const,
  {
    id: 'mat-317-automation-settings-v2',
    type: 'lab',
    name: 'Automation Insights',
    description: 'A new UX for the automation tab in queue settings',
    owner: 'Serhan Guney',
    groups: ['admin', 'organization_group_admin', 'empowered_manager'],
    icon: IconBolt,
  } as const,
  {
    id: 'integration-test-flag',
    type: 'internal',
    description: 'Feature flag used in integration tests',
    owner: 'Jan Pfajfr',
  } as const,
  {
    id: 'send-metrics-to-prometheus',
    type: 'internal',
    description: 'Feature flag used for sending FE telemetry to Prometheus',
    owner: 'Jan Pfajfr',
  } as const,
  {
    id: 'log-metrics-to-prometheus',
    type: 'internal',
    description:
      'Feature flag used to print out raw metrics which are sent to prometheus',
    owner: 'Jan Pfajfr',
  } as const,
  {
    id: 'ac-4343-forrester-demo-custom-document-type',
    type: 'internal',
    description: 'Enables custom document type template when creating queue',
    owner: 'Ondrej Matejka',
  } as const,
  {
    id: 'ac-4438-popup-for-trusted-buttons',
    type: 'internal',
    description: 'Enables sharing token with 3rd party apps via buttons.',
    epic: 'AC-4438',
    owner: 'Pavel Sury',
  } as const,
  {
    id: 'xmas',
    type: 'internal',
    description: 'Enables Xmas mode',
    epic: '-',
    owner: 'Martin Brodziansky',
  } as const,
  {
    id: 'aggressive-dashboard-refetching',
    type: 'internal',
    description:
      'Enables aggressive refetching of dashboard data from `/search` after a document is imported',
    epic: '-',
    owner: 'Martin Brodziansky',
  } as const,
  {
    id: 'smartlook-percentage-rollout',
    type: 'internal',
    description:
      'Used to limit smartlook initialization by percent rollout strategy',
    epic: '-',
    owner: 'Jan Pfajfr',
  } as const,
  {
    id: 'smartlook-whitelist',
    type: 'internal',
    description: 'Used to enables smartlook for specific organization',
    epic: '-',
    owner: 'Jan Pfajfr',
  } as const,
  {
    id: 'hypercare-metrics',
    type: 'internal',
    description:
      'Used to enables metrics with labels for specific organization',
    epic: '-',
    owner: 'Jan Pfajfr',
  } as const,
  {
    id: 'rule-manager',
    type: 'lab',
    groups: ['admin', 'organization_group_admin'],
    description: 'Manage rules accross queues',
    name: 'Rule manager',
    epic: 'PAT-347',
    owner: 'Ondrej Matejka',
    icon: IconSTurnRight,
  } as const,
  {
    id: 'zen-mode',
    type: 'internal',
    description: 'Enables hidden navbar and user panel across the app',
    epic: 'MAT-520',
    owner: 'Filip Fabisik',
  } as const,
  {
    id: 'email-templates',
    type: 'internal',
    description:
      'Allows to use e-mail templates (enabled for non-trial users by default)',
    name: 'Email templates',
    epic: 'PAT-249',
    owner: 'Pavel Sury',
  } as const,
  {
    id: 'mr-fabry',
    type: 'lab',
    name: 'Rossum Agent',
    description: 'Enables the Agentic Chat UI powered by Rossum Agent',
    owner: 'PAT',
  } as const,
  {
    id: 'mr-fabry-write',
    type: 'internal',
    description: 'Adds write mode to Rossum Agent',
    owner: 'PAT',
  } as const,
  {
    id: 'mr-fabry-coupacabry',
    type: 'lab',
    name: 'Rossum Agent 2.0',
    description: 'Different (hopefully better) backend for Rossum Agent.',
    owner: 'PAT',
    icon: IconMessageCirclePlus,
  } as const,
  {
    id: 'pat-645-lookup-agent',
    type: 'internal',
    description:
      'Routes the lookup field suggester through Rossum Agent, so suggestions can be refined in a conversation instead of being one-shot.',
    owner: 'PAT',
  } as const,
  {
    id: 'mat-1113-freshdesk-chat',
    type: 'internal',
    name: 'Freshdesk chat',
    description: 'Enables Freshdesk chat',
    owner: 'Martin Brodziansky',
  } as const,
  {
    id: 'configuration-map',
    type: 'lab',
    name: 'Dependency Graph',
    description:
      'Visual dependency graph of queue field configurations, hooks, and rules',
    owner: 'Michal Stanek',
    groups: ['admin', 'organization_group_admin'],
  } as const,
  {
    id: 'demo-mode',
    type: 'internal',
    description:
      'Enables demo mode - forces Rossum logo and disables custom branding',
    owner: 'Jan Pfajfr',
  } as const,
  {
    id: 'neighbors-drawer',
    type: 'lab',
    name: 'Show pages that influenced predictions',
    description:
      'Shows which pages influenced predictions, per page in the document view',
    owner: 'Petr Baudis',
    groups: ['admin', 'organization_group_admin'],
    icon: IconLayoutGrid,
  } as const,
  {
    id: 'document-comments',
    type: 'internal',
    description:
      'A feature flag that enables Document Comments where users can create/edit/delete comments on the document. Initially available on annotation view.',
    epic: 'MAT-1709',
    owner: 'Serhan Guney',
  } as const,
  {
    id: 'integration-credentials',
    type: 'internal',
    description:
      'Enables the centralized integration credentials page in settings, where OAuth2 credentials for integrations (Coupa first) are stored once per organization instead of being pasted into every hook.',
    epic: 'MAT-2101',
    owner: 'Serhan Guney',
  } as const,
  {
    id: 'pat-improved-requests-dashboard',
    type: 'internal',
    description: 'Filtering and custom columns for approvers',
    name: 'Improved requests dashboard',
    owner: 'Pavel Sury',
  } as const,
];

// To avoid circular dep when determining FeatureId type
export const allFeaturesList: (InternalFeature | LabFeature)[] =
  allFeaturesListBase;

type BaseFeature = {
  id: FeatureId;
  type: 'internal' | 'lab';
  description: string;
  owner?: string;
  epic?: string;
  icon?: ForwardRefExoticComponent<IconProps>;
};

export type InternalFeature = Prettify<
  BaseFeature & {
    type: 'internal';
  }
>;

export const isInternalFeature = (
  feature: Feature
): feature is InternalFeature => feature.type === 'internal';

export type LabFeature = Prettify<
  BaseFeature & {
    type: 'lab';
    name: string;
    groups?: Readonly<GroupRole[]>;
    gaDate?: string;
  }
>;

export const isLabFeature = (feature: Feature): feature is LabFeature =>
  feature.type === 'lab';

export type Feature = InternalFeature | LabFeature;

export const allFeatures = R.indexBy(allFeaturesList, ({ id }) => id);

export type FeatureId = (typeof allFeaturesListBase)[number]['id'];

export const labFeaturesList = R.pipe(allFeaturesList, R.filter(isLabFeature));

export const labFeatures = R.pipe(
  labFeaturesList,
  R.indexBy(({ id }) => id)
);

export const internalFeaturesList = R.pipe(
  allFeaturesList,
  R.filter(isInternalFeature)
);
